/**
 * Rumsrader i arbetsbeskrivningen.
 *
 * En punkt som gäller flera rum listar dem på egna rader under sig:
 *
 *   • Innertak (totalt ca 14,1 kvm golvyta) målas...   yrkesgrupp: Finisharbete
 *   - Hall 5trp (ca 5,3 kvm golvyta).                  yrkesgrupp: (tom)
 *   - Hall 6trp (ca 8,8 kvm golvyta).                  yrkesgrupp: (tom)
 *
 * Rumsraderna saknar egen yrkesgrupp eftersom de inte är egna arbetsmoment —
 * priset ligger på punkten. Men det gjorde att de föll bort vid filtrering på
 * yrkesgrupp och blev ofärgade, så filtret visade punkten utan att avslöja
 * vilka rum den gällde.
 *
 * Kedjeregeln är densamma som computeSubRowIdSet i KalkylTable_SIMPLIFIED
 * använder för att flytta rumsrader tillsammans med sin punkt: en "-"-rad hör
 * till närmast föregående "•"-rad, och kedjan bryts av rubriker, tomrader och
 * av en "-"-rad som inte har någon "•"-punkt före sig.
 *
 * Uppmätt i produktion: 2 110 rader inleds med "-", varav 2 068 följer på en
 * punkt med yrkesgrupp. Övriga 42 saknar ägare och lämnas orörda.
 */

/** Är raden ett innehållsitem (inte rubrik eller tomrad)? */
const arInnehall = (item) =>
  item && !item.isMainHeader && !item.isSubHeader && !item.isEmpty;

/** Yrkesgruppen räknas bara om den är satt — "0" förekommer som tomvärde. */
const giltigYrkesgrupp = (yg) => (yg && yg !== 0 && yg !== '0' ? yg : null);

/**
 * Låter rumsrader ärva yrkesgrupp från punkten de hör till.
 *
 * Returnerar en ny array; ingen indata muteras. Rader som redan har en
 * yrkesgrupp lämnas orörda, så funktionen är idempotent och kan köras både
 * före filtrering och före rendering.
 *
 * Ärvda rader märks med `yrkesgruppArvd: true` så de går att skilja från
 * riktiga arbetsmoment — de har inget eget pris.
 *
 * @param {Array} items - Arbetsbeskrivningsrader med `text` och `yrkesgrupp`
 * @returns {Array}
 */
export const medArvdYrkesgrupp = (items) => {
  if (!Array.isArray(items)) return items;

  let agareYrkesgrupp = null;

  return items.map(item => {
    if (!arInnehall(item)) {
      agareYrkesgrupp = null;
      return item;
    }

    const text = (item.text || '').trim();

    if (text.startsWith('•')) {
      agareYrkesgrupp = giltigYrkesgrupp(item.yrkesgrupp);
      return item;
    }

    if (text.startsWith('-')) {
      // Flera rumsrader i följd hör alla till samma punkt, så kedjan hålls öppen
      if (agareYrkesgrupp && !giltigYrkesgrupp(item.yrkesgrupp)) {
        return { ...item, yrkesgrupp: agareYrkesgrupp, yrkesgruppArvd: true };
      }
      return item;
    }

    agareYrkesgrupp = null;
    return item;
  });
};

/** De fem yrkesgrupper som legenden slår ihop under nyckeln 'admin'. */
export const ADMIN_YRKESGRUPPER = [
  'Arbetsledning', 'Framkörning', 'Bystning', 'Grovstäd', 'Sophantering'
];

/**
 * Filtrerar arbetsbeskrivningen till rader som matchar någon av de valda
 * yrkesgrupperna. Huvud- och underrubriker följer med när de har minst en
 * matchande rad under sig, så strukturen går att läsa.
 *
 * Rumsraderna ärver punktens yrkesgrupp först — annars visade filtret punkten
 * utan att avslöja vilka rum den gällde.
 *
 * Låg tidigare som två identiska kopior, i offertens WorkDescriptionSection och
 * i BuilderPUView. Den ena hann rättas medan den andra inte gjorde det.
 *
 * @param {Array} rawItems - Arbetsbeskrivningsrader
 * @param {Set<string>} keys - Valda yrkesgrupp-nycklar (kan innehålla 'admin')
 * @returns {Array}
 */
export const filtreraPaYrkesgrupp = (rawItems, keys) => {
  if (!keys || keys.size === 0 || !Array.isArray(rawItems)) return rawItems;

  const items = medArvdYrkesgrupp(rawItems);
  const includeAdmin = keys.has('admin');
  const matchar = (yg) => {
    if (!yg) return false;
    if (includeAdmin && ADMIN_YRKESGRUPPER.includes(yg)) return true;
    return keys.has(yg);
  };

  const result = [];
  let pendingMain = null;
  let mainEmitted = false;
  let pendingSub = null;
  let subEmitted = false;

  items.forEach(item => {
    if (item.isMainHeader) {
      pendingMain = item;
      mainEmitted = false;
      pendingSub = null;
      subEmitted = false;
      return;
    }
    if (item.isSubHeader) {
      pendingSub = item;
      subEmitted = false;
      return;
    }
    if (item.isEmpty) return;

    if (matchar(item.yrkesgrupp)) {
      if (pendingMain && !mainEmitted) {
        result.push(pendingMain);
        mainEmitted = true;
      }
      if (pendingSub && !subEmitted) {
        result.push(pendingSub);
        subEmitted = true;
      }
      result.push(item);
    }
  });

  return result;
};
